import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "专题", to: "/special/silent-current" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setKeyword("");
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="container-portal flex h-16 items-center justify-between gap-4 md:h-[72px]">
        <Link to="/" className="flex items-center gap-2.5 shrink-0">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-[12px] font-black leading-none text-background-50">
            人兽
          </span>
          <span className="flex flex-col leading-none">
            <span className="text-[17px] font-bold tracking-tight text-foreground-950">人兽影视网</span>
            <span className="label-caps mt-1 text-[9px] text-foreground-500">Renshou Film</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                [
                  "relative px-3.5 py-2 text-sm transition-colors cursor-pointer",
                  isActive
                    ? "text-primary-700 font-semibold"
                    : "text-foreground-700 hover:text-primary-600",
                ].join(" ")
              }
            >
              {({ isActive }) => (
                <span className="flex flex-col items-center">
                  {item.label}
                  <span
                    className={[
                      "mt-1 h-[2px] w-4 rounded-full transition-colors",
                      isActive ? "bg-accent-500" : "bg-transparent",
                    ].join(" ")}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className="hidden lg:flex items-center h-9 w-[248px] rounded-full border border-background-300 bg-background-100 pl-3.5 pr-1 focus-within:border-primary-400 transition-colors"
          >
            <i className="ri-search-line text-[15px] text-foreground-500" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视、演员、类型"
              className="h-full flex-1 bg-transparent px-2 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              aria-label="站内搜索"
            />
            <button
              type="submit"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50 cursor-pointer hover:bg-primary-600 transition-colors"
              aria-label="提交搜索"
            >
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>

          <button
            type="button"
            onClick={() => setSearchOpen((prev) => !prev)}
            className="flex lg:hidden h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-700 cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
            aria-label="展开搜索"
          >
            <i className={searchOpen ? "ri-close-line" : "ri-search-line"} />
          </button>

          <button
            type="button"
            onClick={() => setMenuOpen((prev) => !prev)}
            className="flex md:hidden h-9 w-9 items-center justify-center rounded-full border border-background-300 text-foreground-800 cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors"
            aria-label="打开菜单"
            aria-expanded={menuOpen}
          >
            <i className={menuOpen ? "ri-close-line text-lg" : "ri-menu-3-line text-lg"} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-3 animate-fade-in">
          <form
            onSubmit={handleSearch}
            className="flex h-10 w-full items-center rounded-full border border-background-300 bg-background-100 pl-3.5 pr-1"
          >
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视、演员、类型"
              className="h-full flex-1 bg-transparent px-2 text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
              aria-label="站内搜索"
            />
            <button
              type="submit"
              className="flex h-8 items-center justify-center rounded-full bg-primary-500 px-3 text-xs text-background-50 cursor-pointer"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 pb-4 pt-2 animate-fade-in">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [
                    "flex items-center justify-between border-b border-background-200/70 py-3 text-[15px] cursor-pointer",
                    isActive ? "text-primary-700 font-semibold" : "text-foreground-800",
                  ].join(" ")
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400" />
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}